import { useCallback, useEffect, useRef, useState } from "react";
import { ASHLEY_PROMPTS } from "../speech/ashleyPrompts";
import {
  cancelAshleySpeech,
  isAshleySpeechSupported,
  speakAshley,
} from "../speech/ashleySpeech";
import { POST_GREETING_LISTEN_DELAY_MS } from "../speech/voiceNavigationTiming";
import { useAshleyListening } from "../voice/recognition/useAshleyListening";
import {
  classifyWorkflowCommand,
  GUIDED_WORKFLOW_COMMAND,
} from "../forms/guidedWorkflowCommands";
import {
  matchDropdownOption,
  normalizePhone,
  extractEmailFromVoiceTranscript,
} from "../forms/parseVoiceFieldValue";
import { ashleyLog } from "../debug/ashleySttDebug";
import {
  registerGuidedWorkflow,
  unregisterGuidedWorkflow,
} from "../forms/guidedWorkflowRegistry";

export const CREATE_PROJECT_VOICE_PHASE = Object.freeze({
  SPEAKING: "speaking",
  LISTENING: "listening",
  WAITING: "waiting",
  CONFIRMING: "confirming",
});

const PROMPTS = Object.freeze({
  CLIENT_ASK: "Who is this project for?",
  CLIENT_NOT_FOUND:
    "I couldn't find that client. Please say it again, or create the client first if it doesn't exist.",
  CLIENT_TRY_AGAIN: "Okay, let's try again.",
  CLIENT_PERFECT: "Perfect.",
  PROJECT_NAME_ASK: "What is the project name?",
  PROJECT_NAME_RETRY: "Sorry about that. Please say it again.",
  CONTACT_NAME_ASK: "Who is the primary contact for this project?",
  CONTACT_METHOD_ASK: "How should we contact them?",
  CONTACT_METHOD_RETRY: "I'm sorry, could you say that again?",
  PHONE_ALSO_ASK: "Do we have a phone number as well?",
  EMAIL_ALSO_ASK: "Do we have an email address as well?",
  NOTES_ASK: "Is there anything you'd like to add to the project notes?",
  SKIP_ACK: "Okay.",
  CLOSING_ASK:
    "Perfect. I have everything I need. Would you like me to save the project now, or would you prefer to finish the remaining details manually?",
  SAVE_SUCCESS: "Your project has been created successfully.",
  MANUAL_ACK:
    "No problem. You can finish the remaining details whenever you're ready.",
});

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function speakAndWait(message) {
  return new Promise((resolve) => {
    if (!isAshleySpeechSupported()) {
      resolve();
      return;
    }

    speakAshley(message, {
      onEnd: resolve,
      onError: resolve,
    });
  });
}

function scrollToVoiceField(fieldId) {
  if (!fieldId) {
    return;
  }

  const element = document.querySelector(`[data-voice-field="${fieldId}"]`);
  element?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function getClientLabel(clientOptions, clientId) {
  return clientOptions.find((option) => option.value === clientId)?.label ?? "";
}

function buildClientConfirmMessage(label) {
  return `I have ${label}. Is that correct?`;
}

function buildProjectNameConfirmMessage(name) {
  return `I heard ${name}. Is that correct?`;
}

function looksLikePhone(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) {
    return false;
  }

  const digits = trimmed.replace(/\D/g, "");
  return digits.length >= 7;
}

export function parseConfidentContactInput(transcript) {
  const emailValue = extractEmailFromVoiceTranscript(transcript);
  if (emailValue) {
    return {
      fieldId: "clientContactEmail",
      value: emailValue,
    };
  }

  if (looksLikePhone(transcript)) {
    return {
      fieldId: "clientContactPhone",
      value: normalizePhone(transcript),
    };
  }

  return null;
}

export function parseConfidentPhoneInput(transcript) {
  if (!looksLikePhone(transcript)) {
    return null;
  }

  return {
    fieldId: "clientContactPhone",
    value: normalizePhone(transcript),
  };
}

export function parseConfidentEmailInput(transcript) {
  const emailValue = extractEmailFromVoiceTranscript(transcript);
  if (!emailValue) {
    return null;
  }

  return {
    fieldId: "clientContactEmail",
    value: emailValue,
  };
}

function isOptionalFieldSkip(transcript) {
  const command = classifyWorkflowCommand(transcript);
  return (
    command === GUIDED_WORKFLOW_COMMAND.SKIP ||
    command === GUIDED_WORKFLOW_COMMAND.NO
  );
}

function normalizeClosingTranscript(transcript) {
  return String(transcript || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function classifyClosingResponse(transcript) {
  const normalized = normalizeClosingTranscript(transcript);
  if (!normalized) {
    return null;
  }

  if (
    normalized === "save" ||
    normalized === "save project" ||
    normalized.startsWith("save ") ||
    normalized.includes("save project") ||
    normalized === "yes" ||
    normalized === "go ahead" ||
    normalized.includes("yes save") ||
    normalized.includes("save it") ||
    normalized.includes("thats everything") ||
    normalized.includes("that s everything")
  ) {
    return "save";
  }

  if (
    normalized === "manual" ||
    normalized === "no" ||
    normalized === "not yet" ||
    normalized.includes("finish manually") ||
    normalized.includes("finish it manually") ||
    normalized.includes("ill finish it") ||
    normalized.includes("i ll finish it")
  ) {
    return "manual";
  }

  return null;
}

/**
 * Hardcoded Create Project voice MVP — client, project name, and contact steps.
 */
export function useCreateProjectVoiceMvp({
  enabled,
  setClient,
  clientOptions,
  setProjectName,
  setClientContactName,
  setClientContactPhone,
  setClientContactEmail,
  setNotes,
  onSaveProject,
  onComplete,
}) {
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
  const [statusMessage, setStatusMessage] = useState("");
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [interactionUI, setInteractionUI] = useState(null);

  const runningRef = useRef(false);
  const cancelledRef = useRef(false);
  const voiceSettingRef = useRef(false);
  const transcriptHandlerRef = useRef(null);
  const errorHandlerRef = useRef(null);
  const manualClientResolverRef = useRef(null);
  const confirmResolverRef = useRef(null);
  const listeningControlsRef = useRef(null);
  const clientOptionsRef = useRef(clientOptions);
  const sessionHandleRef = useRef({
    cancel: () => {},
    isActive: () => false,
  });

  clientOptionsRef.current = clientOptions;

  const listenOnce = useCallback(() => {
    return new Promise((resolve, reject) => {
      transcriptHandlerRef.current = (transcript) => {
        transcriptHandlerRef.current = null;
        errorHandlerRef.current = null;
        resolve(transcript);
      };

      errorHandlerRef.current = (code) => {
        transcriptHandlerRef.current = null;
        errorHandlerRef.current = null;
        reject(new Error(code || "recognition_error"));
      };

      const started = listeningControlsRef.current?.startListening();
      if (started === false) {
        reject(new Error("unsupported"));
      }
    });
  }, []);

  const listening = useAshleyListening({
    onFinalTranscript: (transcript) => {
      transcriptHandlerRef.current?.(transcript);
    },
    onError: (code) => {
      errorHandlerRef.current?.(code);
    },
  });

  listeningControlsRef.current = listening;

  const stopSession = useCallback(() => {
    cancelledRef.current = true;
    runningRef.current = false;
    cancelAshleySpeech();
    listeningControlsRef.current?.abortListening();
    transcriptHandlerRef.current = null;
    errorHandlerRef.current = null;
    manualClientResolverRef.current = null;
    confirmResolverRef.current = null;
    unregisterGuidedWorkflow(sessionHandleRef.current);
    setIsActive(false);
    setActiveFieldId(null);
    setInteractionUI(null);
    setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
    setStatusMessage("");
  }, []);

  sessionHandleRef.current.cancel = stopSession;
  sessionHandleRef.current.isActive = () => runningRef.current;

  const resolveManualClient = useCallback((value) => {
    manualClientResolverRef.current?.({ type: "manual", value });
    manualClientResolverRef.current = null;
  }, []);

  const waitForManualClient = useCallback(() => {
    return new Promise((resolve) => {
      manualClientResolverRef.current = resolve;
    });
  }, []);

  const waitForConfirmation = useCallback(() => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
    });
  }, []);

  const resolveConfirmation = useCallback((accepted) => {
    confirmResolverRef.current?.(accepted);
    confirmResolverRef.current = null;
  }, []);

  const handleListenError = useCallback(async (code) => {
    if (code === "no-speech") {
      return "retry";
    }

    if (code === "unsupported") {
      setStatusMessage(ASHLEY_PROMPTS.UNSUPPORTED_BROWSER);
      return "stop";
    }

    if (code === "not-allowed") {
      setStatusMessage(ASHLEY_PROMPTS.MIC_DENIED);
      return "stop";
    }

    return "retry";
  }, []);

  const collectClientWithManual = useCallback(async () => {
    while (runningRef.current && !cancelledRef.current) {
      setPhase(CREATE_PROJECT_VOICE_PHASE.LISTENING);
      setStatusMessage(PROMPTS.CLIENT_ASK);

      const manualPromise = waitForManualClient();
      const listenPromise = listenOnce()
        .then((transcript) => ({ type: "voice", transcript }))
        .catch(async (error) => {
          const outcome = await handleListenError(error?.message);
          return { type: "error", outcome };
        });

      const result = await Promise.race([manualPromise, listenPromise]);
      listeningControlsRef.current?.abortListening();

      if (result?.type === "manual") {
        return { type: "manual" };
      }

      if (result?.type === "error") {
        if (result.outcome === "stop") {
          return null;
        }
        continue;
      }

      const matched = matchDropdownOption(
        result.transcript,
        clientOptionsRef.current,
      );
      if (matched) {
        voiceSettingRef.current = true;
        setClient(matched);
        voiceSettingRef.current = false;
        return { type: "matched", clientId: matched };
      }

      if (String(result.transcript || "").trim()) {
        setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.CLIENT_NOT_FOUND);
        await speakAndWait(PROMPTS.CLIENT_NOT_FOUND);
        await delay(POST_GREETING_LISTEN_DELAY_MS);
      }
    }

    return null;
  }, [handleListenError, listenOnce, setClient, waitForManualClient]);

  const confirmValue = useCallback(
    async (message, summaryLabel, onReject) => {
      setPhase(CREATE_PROJECT_VOICE_PHASE.CONFIRMING);
      setStatusMessage(message);
      setInteractionUI({
        type: "confirm",
        message,
        summaryLabel,
      });

      await speakAndWait(message);

      if (cancelledRef.current || !runningRef.current) {
        return null;
      }

      await delay(POST_GREETING_LISTEN_DELAY_MS);

      while (runningRef.current && !cancelledRef.current) {
        setPhase(CREATE_PROJECT_VOICE_PHASE.LISTENING);
        setStatusMessage(message);

        const buttonPromise = waitForConfirmation().then((accepted) => ({
          type: "button",
          accepted,
        }));
        const listenPromise = listenOnce()
          .then((transcript) => ({ type: "voice", transcript }))
          .catch(async (error) => {
            const outcome = await handleListenError(error?.message);
            return { type: "error", outcome };
          });

        const result = await Promise.race([buttonPromise, listenPromise]);
        listeningControlsRef.current?.abortListening();

        if (result?.type === "button") {
          setInteractionUI(null);
          if (result.accepted) {
            return true;
          }
          onReject?.();
          return false;
        }

        if (result?.type === "error") {
          if (result.outcome === "stop") {
            return null;
          }
          continue;
        }

        const command = classifyWorkflowCommand(result.transcript);
        if (command === GUIDED_WORKFLOW_COMMAND.YES) {
          setInteractionUI(null);
          return true;
        }

        if (command === GUIDED_WORKFLOW_COMMAND.NO) {
          setInteractionUI(null);
          onReject?.();
          return false;
        }
      }

      return null;
    },
    [handleListenError, listenOnce, waitForConfirmation],
  );

  const collectProjectName = useCallback(async () => {
    while (runningRef.current && !cancelledRef.current) {
      setPhase(CREATE_PROJECT_VOICE_PHASE.LISTENING);
      setStatusMessage(PROMPTS.PROJECT_NAME_ASK);

      let transcript;
      try {
        transcript = await listenOnce();
      } catch (error) {
        const outcome = await handleListenError(error?.message);
        if (outcome === "stop") {
          return null;
        }
        continue;
      }

      const name = String(transcript || "").trim();
      if (!name) {
        continue;
      }

      voiceSettingRef.current = true;
      setProjectName(name);
      voiceSettingRef.current = false;
      return name;
    }

    return null;
  }, [handleListenError, listenOnce, setProjectName]);

  const runClientStep = useCallback(async () => {
    while (runningRef.current && !cancelledRef.current) {
      setActiveFieldId("client");
      scrollToVoiceField("client");

      setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
      setStatusMessage(PROMPTS.CLIENT_ASK);
      await speakAndWait(PROMPTS.CLIENT_ASK);

      if (cancelledRef.current || !runningRef.current) {
        return false;
      }

      await delay(POST_GREETING_LISTEN_DELAY_MS);

      const collectResult = await collectClientWithManual();
      if (!collectResult) {
        return false;
      }

      if (collectResult.type === "manual") {
        setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.CLIENT_PERFECT);
        await speakAndWait(PROMPTS.CLIENT_PERFECT);
        setActiveFieldId(null);
        return true;
      }

      const label = getClientLabel(
        clientOptionsRef.current,
        collectResult.clientId,
      );
      const confirmMessage = buildClientConfirmMessage(label);
      const confirmed = await confirmValue(confirmMessage, label, () => {
        voiceSettingRef.current = true;
        setClient("");
        voiceSettingRef.current = false;
      });

      if (confirmed === true) {
        setActiveFieldId(null);
        return true;
      }

      if (confirmed === false) {
        setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.CLIENT_TRY_AGAIN);
        await speakAndWait(PROMPTS.CLIENT_TRY_AGAIN);
        continue;
      }

      return false;
    }

    return false;
  }, [collectClientWithManual, confirmValue, setClient]);

  const runProjectNameStep = useCallback(async () => {
    while (runningRef.current && !cancelledRef.current) {
      setActiveFieldId("projectName");
      scrollToVoiceField("projectName");

      setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
      setStatusMessage(PROMPTS.PROJECT_NAME_ASK);
      await speakAndWait(PROMPTS.PROJECT_NAME_ASK);

      if (cancelledRef.current || !runningRef.current) {
        return false;
      }

      await delay(POST_GREETING_LISTEN_DELAY_MS);

      const name = await collectProjectName();
      if (!name) {
        return false;
      }

      const confirmMessage = buildProjectNameConfirmMessage(name);
      const confirmed = await confirmValue(confirmMessage, name, () => {
        voiceSettingRef.current = true;
        setProjectName("");
        voiceSettingRef.current = false;
      });

      if (confirmed === true) {
        setActiveFieldId(null);
        return true;
      }

      if (confirmed === false) {
        setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.PROJECT_NAME_RETRY);
        await speakAndWait(PROMPTS.PROJECT_NAME_RETRY);
        continue;
      }

      return false;
    }

    return false;
  }, [collectProjectName, confirmValue, setProjectName]);

  const speakPromptAndPrepareListen = useCallback(async (prompt) => {
    setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
    setStatusMessage(prompt);
    await speakAndWait(prompt);

    if (cancelledRef.current || !runningRef.current) {
      return false;
    }

    await delay(POST_GREETING_LISTEN_DELAY_MS);
    return true;
  }, []);

  const handleOptionalSkip = useCallback(async () => {
    setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
    setStatusMessage(PROMPTS.SKIP_ACK);
    await speakAndWait(PROMPTS.SKIP_ACK);
    return true;
  }, []);

  const collectOptionalText = useCallback(
    async (prompt, fieldId, setValue, { acknowledgeSkip = true } = {}) => {
      while (runningRef.current && !cancelledRef.current) {
        setPhase(CREATE_PROJECT_VOICE_PHASE.LISTENING);
        setStatusMessage(prompt);

        let transcript;
        try {
          transcript = await listenOnce();
        } catch (error) {
          const outcome = await handleListenError(error?.message);
          if (outcome === "stop") {
            return null;
          }
          continue;
        }

        if (isOptionalFieldSkip(transcript)) {
          if (acknowledgeSkip) {
            return handleOptionalSkip();
          }
          return true;
        }

        if (classifyWorkflowCommand(transcript) === GUIDED_WORKFLOW_COMMAND.CANCEL) {
          stopSession();
          return null;
        }

        const value = String(transcript || "").trim();
        if (!value) {
          continue;
        }

        voiceSettingRef.current = true;
        setValue(value);
        voiceSettingRef.current = false;
        return true;
      }

      return null;
    },
    [handleListenError, handleOptionalSkip, listenOnce, stopSession],
  );

  const collectPrimaryContactMethod = useCallback(
    async (prompt) => {
      while (runningRef.current && !cancelledRef.current) {
        setPhase(CREATE_PROJECT_VOICE_PHASE.LISTENING);
        setStatusMessage(prompt);

        let transcript;
        try {
          transcript = await listenOnce();
        } catch (error) {
          const outcome = await handleListenError(error?.message);
          if (outcome === "stop") {
            return null;
          }
          continue;
        }

        if (isOptionalFieldSkip(transcript)) {
          await handleOptionalSkip();
          return { type: "skip" };
        }

        if (classifyWorkflowCommand(transcript) === GUIDED_WORKFLOW_COMMAND.CANCEL) {
          stopSession();
          return null;
        }

        ashleyLog("Create Project contact info — raw transcript:", transcript);
        console.log("Raw transcript:", JSON.stringify(transcript));

        const routed = parseConfidentContactInput(transcript);
        if (!routed) {
          if (!String(transcript || "").trim()) {
            continue;
          }

          setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
          setStatusMessage(PROMPTS.CONTACT_METHOD_RETRY);
          await speakAndWait(PROMPTS.CONTACT_METHOD_RETRY);
          await delay(POST_GREETING_LISTEN_DELAY_MS);
          continue;
        }

        voiceSettingRef.current = true;
        if (routed.fieldId === "clientContactEmail") {
          setClientContactEmail(routed.value);
        } else {
          setClientContactPhone(routed.value);
        }
        voiceSettingRef.current = false;

        return {
          type: routed.fieldId === "clientContactEmail" ? "email" : "phone",
        };
      }

      return null;
    },
    [
      handleListenError,
      handleOptionalSkip,
      listenOnce,
      setClientContactEmail,
      setClientContactPhone,
      stopSession,
    ],
  );

  const collectOptionalPhoneFollowUp = useCallback(
    async (prompt) => {
      while (runningRef.current && !cancelledRef.current) {
        setPhase(CREATE_PROJECT_VOICE_PHASE.LISTENING);
        setStatusMessage(prompt);

        let transcript;
        try {
          transcript = await listenOnce();
        } catch (error) {
          const outcome = await handleListenError(error?.message);
          if (outcome === "stop") {
            return null;
          }
          continue;
        }

        if (isOptionalFieldSkip(transcript)) {
          return { type: "skip" };
        }

        if (classifyWorkflowCommand(transcript) === GUIDED_WORKFLOW_COMMAND.CANCEL) {
          stopSession();
          return null;
        }

        ashleyLog("Create Project phone follow-up — raw transcript:", transcript);
        console.log("Raw transcript:", JSON.stringify(transcript));

        const routed = parseConfidentPhoneInput(transcript);
        if (!routed) {
          if (!String(transcript || "").trim()) {
            continue;
          }

          setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
          setStatusMessage(PROMPTS.CONTACT_METHOD_RETRY);
          await speakAndWait(PROMPTS.CONTACT_METHOD_RETRY);
          await delay(POST_GREETING_LISTEN_DELAY_MS);
          continue;
        }

        voiceSettingRef.current = true;
        setClientContactPhone(routed.value);
        voiceSettingRef.current = false;
        return { type: "phone" };
      }

      return null;
    },
    [handleListenError, listenOnce, setClientContactPhone, stopSession],
  );

  const collectOptionalEmailFollowUp = useCallback(
    async (prompt) => {
      while (runningRef.current && !cancelledRef.current) {
        setPhase(CREATE_PROJECT_VOICE_PHASE.LISTENING);
        setStatusMessage(prompt);

        let transcript;
        try {
          transcript = await listenOnce();
        } catch (error) {
          const outcome = await handleListenError(error?.message);
          if (outcome === "stop") {
            return null;
          }
          continue;
        }

        if (isOptionalFieldSkip(transcript)) {
          return { type: "skip" };
        }

        if (classifyWorkflowCommand(transcript) === GUIDED_WORKFLOW_COMMAND.CANCEL) {
          stopSession();
          return null;
        }

        ashleyLog("Create Project email follow-up — raw transcript:", transcript);
        console.log("Raw transcript:", JSON.stringify(transcript));

        const routed = parseConfidentEmailInput(transcript);
        if (!routed) {
          if (!String(transcript || "").trim()) {
            continue;
          }

          setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
          setStatusMessage(PROMPTS.CONTACT_METHOD_RETRY);
          await speakAndWait(PROMPTS.CONTACT_METHOD_RETRY);
          await delay(POST_GREETING_LISTEN_DELAY_MS);
          continue;
        }

        voiceSettingRef.current = true;
        setClientContactEmail(routed.value);
        voiceSettingRef.current = false;
        return { type: "email" };
      }

      return null;
    },
    [handleListenError, listenOnce, setClientContactEmail, stopSession],
  );

  const runContactNameStep = useCallback(async () => {
    setActiveFieldId("clientContactName");
    scrollToVoiceField("clientContactName");

    const ready = await speakPromptAndPrepareListen(PROMPTS.CONTACT_NAME_ASK);
    if (!ready) {
      return false;
    }

    const result = await collectOptionalText(
      PROMPTS.CONTACT_NAME_ASK,
      "clientContactName",
      setClientContactName,
    );
    setActiveFieldId(null);
    return Boolean(result);
  }, [
    collectOptionalText,
    setClientContactName,
    speakPromptAndPrepareListen,
  ]);

  const runContactInfoStep = useCallback(async () => {
    setActiveFieldId("contactMethod");
    scrollToVoiceField("contactMethod");

    const ready = await speakPromptAndPrepareListen(PROMPTS.CONTACT_METHOD_ASK);
    if (!ready) {
      return false;
    }

    const primary = await collectPrimaryContactMethod(PROMPTS.CONTACT_METHOD_ASK);
    if (!primary) {
      return false;
    }

    if (primary.type === "skip") {
      setActiveFieldId(null);
      return true;
    }

    if (primary.type === "email") {
      setActiveFieldId("clientContactPhone");
      scrollToVoiceField("clientContactPhone");

      const phoneReady = await speakPromptAndPrepareListen(PROMPTS.PHONE_ALSO_ASK);
      if (!phoneReady) {
        return false;
      }

      const phoneFollowUp = await collectOptionalPhoneFollowUp(
        PROMPTS.PHONE_ALSO_ASK,
      );
      if (!phoneFollowUp) {
        return false;
      }
    }

    if (primary.type === "phone") {
      setActiveFieldId("clientContactEmail");
      scrollToVoiceField("clientContactEmail");

      const emailReady = await speakPromptAndPrepareListen(PROMPTS.EMAIL_ALSO_ASK);
      if (!emailReady) {
        return false;
      }

      const emailFollowUp = await collectOptionalEmailFollowUp(
        PROMPTS.EMAIL_ALSO_ASK,
      );
      if (!emailFollowUp) {
        return false;
      }
    }

    setActiveFieldId(null);
    return true;
  }, [
    collectOptionalEmailFollowUp,
    collectOptionalPhoneFollowUp,
    collectPrimaryContactMethod,
    speakPromptAndPrepareListen,
  ]);

  const runProjectNotesStep = useCallback(async () => {
    setActiveFieldId("notes");
    scrollToVoiceField("notes");

    const ready = await speakPromptAndPrepareListen(PROMPTS.NOTES_ASK);
    if (!ready) {
      return false;
    }

    const result = await collectOptionalText(
      PROMPTS.NOTES_ASK,
      "notes",
      setNotes,
      { acknowledgeSkip: false },
    );
    setActiveFieldId(null);
    return Boolean(result);
  }, [collectOptionalText, setNotes, speakPromptAndPrepareListen]);

  const runClosingStep = useCallback(async () => {
    setActiveFieldId(null);

    const ready = await speakPromptAndPrepareListen(PROMPTS.CLOSING_ASK);
    if (!ready) {
      return false;
    }

    while (runningRef.current && !cancelledRef.current) {
      setPhase(CREATE_PROJECT_VOICE_PHASE.LISTENING);
      setStatusMessage(PROMPTS.CLOSING_ASK);

      let transcript;
      try {
        transcript = await listenOnce();
      } catch (error) {
        const outcome = await handleListenError(error?.message);
        if (outcome === "stop") {
          return false;
        }
        continue;
      }

      if (classifyWorkflowCommand(transcript) === GUIDED_WORKFLOW_COMMAND.CANCEL) {
        stopSession();
        return false;
      }

      const decision = classifyClosingResponse(transcript);
      if (decision === "save") {
        const saved = (await onSaveProject?.()) === true;
        if (!saved) {
          continue;
        }

        setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.SAVE_SUCCESS);
        await speakAndWait(PROMPTS.SAVE_SUCCESS);
        return true;
      }

      if (decision === "manual") {
        setPhase(CREATE_PROJECT_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.MANUAL_ACK);
        await speakAndWait(PROMPTS.MANUAL_ACK);
        return true;
      }
    }

    return false;
  }, [handleListenError, listenOnce, onSaveProject, speakPromptAndPrepareListen, stopSession]);

  const runSession = useCallback(async () => {
    if (runningRef.current) {
      return;
    }

    cancelledRef.current = false;
    runningRef.current = true;
    setIsActive(true);
    registerGuidedWorkflow(sessionHandleRef.current);

    const clientOk = await runClientStep();
    if (!clientOk || cancelledRef.current) {
      stopSession();
      return;
    }

    const projectNameOk = await runProjectNameStep();
    if (!projectNameOk || cancelledRef.current) {
      stopSession();
      return;
    }

    const contactNameOk = await runContactNameStep();
    if (!contactNameOk || cancelledRef.current) {
      stopSession();
      return;
    }

    const contactInfoOk = await runContactInfoStep();
    if (!contactInfoOk || cancelledRef.current) {
      stopSession();
      return;
    }

    const notesOk = await runProjectNotesStep();
    if (!notesOk || cancelledRef.current) {
      stopSession();
      return;
    }

    const closingOk = await runClosingStep();
    if (!closingOk || cancelledRef.current) {
      stopSession();
      return;
    }

    stopSession();
    onComplete?.();
  }, [
    onComplete,
    runClientStep,
    runClosingStep,
    runContactInfoStep,
    runContactNameStep,
    runProjectNameStep,
    runProjectNotesStep,
    stopSession,
  ]);

  const runSessionRef = useRef(runSession);
  runSessionRef.current = runSession;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (!runningRef.current) {
        runSessionRef.current();
      }
    }, 600);

    return () => {
      window.clearTimeout(timer);
    };
  }, [enabled]);

  useEffect(() => () => stopSession(), [stopSession]);

  const notifyFieldChange = useCallback(
    (fieldId, value) => {
      if (
        fieldId !== "client" ||
        !runningRef.current ||
        voiceSettingRef.current ||
        !value
      ) {
        return;
      }

      resolveManualClient(value);
    },
    [resolveManualClient],
  );

  const notifyFieldBlur = useCallback(() => {}, []);

  const confirmStepYes = useCallback(() => {
    resolveConfirmation(true);
  }, [resolveConfirmation]);

  const confirmStepNo = useCallback(() => {
    resolveConfirmation(false);
  }, [resolveConfirmation]);

  return {
    isActive,
    phase,
    statusMessage,
    activeFieldId,
    interactionUI,
    notifyFieldChange,
    notifyFieldBlur,
    confirmStepYes,
    confirmStepNo,
    cancel: stopSession,
  };
}

export {
  PROMPTS as CREATE_PROJECT_VOICE_PROMPTS,
  buildClientConfirmMessage,
  buildProjectNameConfirmMessage,
  getClientLabel,
};
